import React from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Crown, Lock, Zap, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { MODULE_DISPLAY_NAMES } from '@/constants/moduleAccess';

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  moduleName: string;
  currentPlan: string;
  requiredPlan: string;
}

export const UpgradeModal: React.FC<UpgradeModalProps> = ({
  isOpen,
  onClose,
  moduleName,
  currentPlan,
  requiredPlan
}) => {
  const navigate = useNavigate();

  const getModuleIcon = (module: string) => {
    switch (module) {
      case 'climate_risk_assessment':
        return <Crown className="h-6 w-6" />;
      case 'double_materiality_assessment':
        return <Lock className="h-6 w-6" />;
      default:
        return <Zap className="h-6 w-6" />;
    }
  };

  const getModuleDisplayName = (module: string) => {
    return MODULE_DISPLAY_NAMES[module as keyof typeof MODULE_DISPLAY_NAMES] || 
           module.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const getPlanIcon = (plan: string) => {
    switch (plan.toLowerCase()) {
      case 'individual':
        return <Zap className="h-5 w-5" />;
      case 'corporate':
        return <Users className="h-5 w-5" />;
      case 'enterprise':
        return <Crown className="h-5 w-5" />;
      default:
        return <Zap className="h-5 w-5" />;
    }
  };

  const handleUpgrade = () => {
    onClose();
    navigate('/settings?tab=advanced');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {getModuleIcon(moduleName)}
            Upgrade Required
          </DialogTitle>
          <DialogDescription>
            This feature requires a higher subscription plan
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-center gap-2 mb-2">
              {getModuleIcon(moduleName)}
              <span className="font-semibold text-lg">
                {getModuleDisplayName(moduleName)}
              </span>
            </div>
            <p className="text-sm text-gray-600">
              Advanced sustainability assessment tools
            </p>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 border rounded-lg">
              <div className="flex items-center gap-2">
                {getPlanIcon(currentPlan)}
                <span className="font-medium">Current Plan: {currentPlan}</span>
              </div>
              <Badge variant="secondary">Limited Access</Badge>
            </div>

            <div className="flex items-center justify-between p-3 border-2 border-blue-500 rounded-lg bg-blue-50">
              <div className="flex items-center gap-2">
                {getPlanIcon(requiredPlan)}
                <span className="font-medium">Required: {requiredPlan}</span>
              </div>
              <Badge className="bg-blue-500">Full Access</Badge>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
            <h4 className="font-semibold text-yellow-800 mb-1">What you'll get with {requiredPlan}:</h4>
            <ul className="text-sm text-yellow-700 space-y-1">
              <li>• Full access to {getModuleDisplayName(moduleName)}</li>
              <li>• Advanced analytics and reporting</li>
              <li>• Priority support</li>
              {requiredPlan === 'Enterprise' && (
                <>
                  <li>• Dedicated account manager</li>
                  <li>• Custom integrations</li>
                  <li>• White-label solutions</li>
                </>
              )}
            </ul>
          </div>
        </div>

        <div className="flex gap-2">
          <Button variant="outline" onClick={onClose} className="flex-1">
            Maybe Later
          </Button>
          <Button onClick={handleUpgrade} className="flex-1">
            Upgrade Now
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}; 